import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloudOutlined, MemoryOutlined, RefreshOutlined, RouterOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import EmptyState from '@/components/common/empty-state';
import {
  filterProcessorsByStatus,
  useListProcessorPoolQuery,
} from '@/state/queries/processor-admin/use-list-processor-pool';
import {
  useCreateAzureProcessorMutation,
  useCreateStandaloneProcessorMutation,
} from '@/state/mutations/processor-admin/use-processor-lifecycle-mutations';
import { usePairProcessorDeviceMutation } from '@/state/mutations/processor-admin/use-pair-processor-device';
import { useNotifications } from '@/hooks/use-notifications';
import ProcessorLifecycleActions, { ProcessorVmCell } from '@/components/processor-admin/processor-lifecycle-actions';
import AgentPairDeviceDialog from '@/components/agent-pools-admin/agent-pair-device-dialog';
import AgentPoolPlayerCell from '@/components/agent-pools-admin/agent-pool-player-cell';
import type { ProcessorPoolMemberDto } from '@/types/dtos/processor/processor-pool.dto';

const STATUS_FILTERS = [
  { i18nKey: 'admin.processorAdmin.status.all', fallback: 'All', value: '' },
  { i18nKey: 'admin.processorAdmin.status.connected', fallback: 'Connected', value: 'connected' },
  { i18nKey: 'admin.processorAdmin.status.offline', fallback: 'Offline', value: 'offline' },
  { i18nKey: 'admin.processorAdmin.status.busy', fallback: 'Busy', value: 'busy' },
  { i18nKey: 'admin.processorAdmin.status.auth', fallback: 'Auth issues', value: 'auth' },
  { i18nKey: 'admin.processorAdmin.status.maintenance', fallback: 'Maintenance', value: 'maintenance' },
  { i18nKey: 'admin.processorAdmin.status.paused', fallback: 'Paused', value: 'paused' },
] as const;

function ConnectionChip({ processor }: { processor: ProcessorPoolMemberDto }) {
  if (processor.opsStatus === 'maintenance') {
    return <Chip size="small" label="Maintenance" color="warning" variant="outlined" />;
  }
  if (processor.opsStatus === 'paused') {
    return <Chip size="small" label="Paused" color="default" variant="outlined" />;
  }
  if (processor.isConnected) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Chip size="small" label="Connected" color="success" variant="outlined" />
        {processor.authHealth === 'jwt_stale' && (
          <Chip size="small" label="JWT stale" color="warning" variant="outlined" />
        )}
      </Stack>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Chip size="small" label="Offline" color="default" variant="outlined" />
      {processor.authHealth === 'jwt_stale' && (
        <Chip size="small" label="JWT stale" color="warning" variant="outlined" />
      )}
    </Stack>
  );
}

function DeploymentChip({ processor }: { processor: ProcessorPoolMemberDto }) {
  if (processor.deployment === 'azure') {
    return <Chip size="small" label="Azure VM" icon={<CloudOutlined />} variant="outlined" />;
  }
  return <Chip size="small" label="Standalone" icon={<RouterOutlined />} variant="outlined" />;
}

async function copyText(value: string) {
  await navigator.clipboard.writeText(value);
}

type ProcessorAdminPanelProps = {
  embedded?: boolean;
};

export default function ProcessorAdminPanel({ embedded = false }: ProcessorAdminPanelProps) {
  const t = useTranslations();
  const notification = useNotifications();
  const [statusFilter, setStatusFilter] = useState('');
  const [pairDialogPlayerId, setPairDialogPlayerId] = useState<string | null>(null);

  const { data: processors = [], isLoading, error, refetch, isFetching } = useListProcessorPoolQuery();
  const { mutateAsync: createAzure, isPending: isCreatingAzure } = useCreateAzureProcessorMutation();
  const { mutateAsync: createStandalone, isPending: isCreatingStandalone } = useCreateStandaloneProcessorMutation();
  const { mutateAsync: pairDevice, isPending: isPairing } = usePairProcessorDeviceMutation();

  const filtered = useMemo(() => filterProcessorsByStatus(processors, statusFilter), [processors, statusFilter]);
  const isCreating = isCreatingAzure || isCreatingStandalone;

  const handleCreateAzure = useCallback(async () => {
    try {
      await createAzure(undefined);
      notification.notifySuccess(
        t('admin.processorAdmin.createAzureSuccess', undefined, 'VM-based processor created and deploying'),
      );
    } catch {
      notification.notifyError(
        t('admin.processorAdmin.createAzureError', undefined, 'Failed to create VM-based processor'),
      );
    }
  }, [createAzure, notification, t]);

  const handleCreateStandalone = useCallback(async () => {
    try {
      const result = await createStandalone(undefined);
      setPairDialogPlayerId(result.playerId);
      notification.notifySuccess(
        t('admin.processorAdmin.createStandaloneSuccess', undefined, 'Standalone processor created — pair with OTP'),
      );
    } catch {
      notification.notifyError(
        t('admin.processorAdmin.createStandaloneError', undefined, 'Failed to create standalone processor'),
      );
    }
  }, [createStandalone, notification, t]);

  const pairDialogLabels = useMemo(
    () => ({
      title: t('admin.processorAdmin.pairTitle', undefined, 'Pair standalone device'),
      pairingCodeHint: t('admin.processorAdmin.pairingCode', undefined, 'Pairing code from Media Processor UI'),
      pair: t('admin.processorAdmin.pair', undefined, 'Pair device'),
      pairing: t('admin.processorAdmin.pairing', undefined, 'Pairing…'),
      cancel: t('common.cancel', undefined, 'Cancel'),
      tokenHint: t(
        'admin.processorAdmin.tokenHint',
        undefined,
        'Token issued — agent should receive token-refresh on socket. Copy for manual setup if needed:',
      ),
      invalidCode: t('admin.processorAdmin.invalidCode', undefined, 'Pairing code must be 6 digits'),
      pairSuccess: t('admin.processorAdmin.pairSuccess', undefined, 'Processor paired'),
      pairError: t('admin.processorAdmin.pairError', undefined, 'Pairing failed'),
      copied: t('common.copied', undefined, 'Copied'),
      copyToken: t('common.copyToken', undefined, 'Copy token'),
    }),
    [t],
  );

  const toolbar = (
    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: embedded ? 1.5 : 0 }}>
      <Button
        size="small"
        variant="contained"
        startIcon={isCreatingAzure ? <CircularProgress size={16} color="inherit" /> : <CloudOutlined />}
        onClick={() => void handleCreateAzure()}
        disabled={isCreating}
      >
        {t('admin.processorAdmin.createAzure', undefined, 'Create VM processor')}
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={isCreatingStandalone ? <CircularProgress size={16} color="inherit" /> : <RouterOutlined />}
        onClick={() => void handleCreateStandalone()}
        disabled={isCreating}
      >
        {t('admin.processorAdmin.createStandalone', undefined, 'Create paired processor')}
      </Button>
      <Tooltip title={t('admin.processorAdmin.refresh', undefined, 'Refresh')}>
        <span>
          <IconButton onClick={() => refetch()} disabled={isFetching} size="small">
            {isFetching ? <CircularProgress size={20} /> : <RefreshOutlined />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const content = (
    <Box sx={{ height: embedded ? 'auto' : '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {!embedded && (
        <PageHeaderBar
          mainContent={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <MemoryOutlined sx={{ fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('admin.processorAdmin.title', undefined, 'Media Processor Pool')}
                </Typography>
              </Box>
              {toolbar}
            </Box>
          }
        />
      )}
      {embedded && toolbar}

      <Alert severity="info" sx={{ mt: embedded ? 0 : 1.5 }}>
        {t(
          'admin.processorAdmin.scaleHint',
          undefined,
          'Add VM-based processors for Azure-hosted capacity, or standalone processors for on-prem / dev pairing.',
        )}{' '}
        {t(
          'admin.processorAdmin.releaseLeaseHint',
          undefined,
          'Use Release & requeue when a processor is busy but not making progress.',
        )}
      </Alert>

      <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
        {STATUS_FILTERS.map((filter) => (
          <Chip
            key={filter.value}
            label={t(filter.i18nKey, undefined, filter.fallback)}
            size="small"
            variant={statusFilter === filter.value ? 'filled' : 'outlined'}
            color={statusFilter === filter.value ? 'primary' : 'default'}
            onClick={() => setStatusFilter(filter.value)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Stack>

      <Card sx={{ mt: 1.5, flex: embedded ? undefined : 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <CardContent
          sx={{
            'flex': 1,
            'display': 'flex',
            'flexDirection': 'column',
            'minHeight': 0,
            'p': 0,
            '&:last-child': { pb: 0 },
          }}
        >
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {t('admin.processorAdmin.error', undefined, 'Error loading processor pool')}
            </Alert>
          )}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : filtered.length === 0 ? (
            <EmptyState
              text={
                statusFilter
                  ? t('admin.processorAdmin.emptyFilter', undefined, 'No processors match this filter')
                  : t('admin.processorAdmin.empty', undefined, 'No media processors yet')
              }
            />
          ) : (
            <TableContainer sx={{ flex: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.processorAdmin.columns.name', undefined, 'Name')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.processorAdmin.columns.type', undefined, 'Type')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.processorAdmin.columns.status', undefined, 'Status')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.processorAdmin.columns.job', undefined, 'Current job')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.processorAdmin.columns.agent', undefined, 'Agent')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.processorAdmin.columns.drain', undefined, 'Drain')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.processorAdmin.columns.vm', undefined, 'VM')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.processorAdmin.columns.actions', undefined, 'Actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((processor) => (
                    <TableRow key={processor.playerId} hover>
                      <TableCell>
                        <AgentPoolPlayerCell
                          name={processor.name || 'Media Processor'}
                          playerId={processor.playerId}
                          pairTooltip={t('admin.processorAdmin.pair', undefined, 'Pair device')}
                          showPair={processor.deployment === 'standalone' && !processor.isConnected}
                          onCopy={() =>
                            void copyText(processor.playerId).then(() =>
                              notification.notifySuccess(t('common.copied', undefined, 'Copied')),
                            )
                          }
                          onPair={() => setPairDialogPlayerId(processor.playerId)}
                        />
                      </TableCell>
                      <TableCell>
                        <DeploymentChip processor={processor} />
                      </TableCell>
                      <TableCell>
                        <ConnectionChip processor={processor} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {processor.currentJobId || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{processor.agentVersion || '—'}</TableCell>
                      <TableCell>
                        {processor.opsStatus === 'maintenance' ? (
                          processor.drainComplete ? (
                            <Chip size="small" label="Drained" color="success" variant="outlined" />
                          ) : (
                            <Chip size="small" label="Draining" color="warning" variant="outlined" />
                          )
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <ProcessorVmCell processor={processor} />
                      </TableCell>
                      <TableCell>
                        <ProcessorLifecycleActions processor={processor} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {pairDialogPlayerId && (
        <AgentPairDeviceDialog
          open
          playerId={pairDialogPlayerId}
          labels={pairDialogLabels}
          isPairing={isPairing}
          onClose={() => setPairDialogPlayerId(null)}
          onPair={pairDevice}
        />
      )}
    </Box>
  );

  if (embedded) return content;
  return <OrganizationContentStack content={content} />;
}
